import SkeletonTable from "@/components/SkeletonTable";
import api from "@/lib/axios";
import { getListPeriode } from "@/services/getData";
import { Periode } from "@/types";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  CheckCircle2,
  Clock,
  Filter,
  Users,
  Check,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getInitials } from "@/lib/utils";

type ValidasiType = {
  jml_belum_valid_final: number;
  jml_sudah_valid_final: number;
  nama_lengkap: string;
  total_verifikasi: number;
  user_id: number;
};

const rekapVerifikasiUkt = async (id_sys_periode?: string[] | number[]) => {
  const response = await api.post("/verifikator/peserta/rekap", {
    id_sys_periode,
  });
  return response?.data?.data;
};

export default function RekapVerifikasiPage() {
  const [selectedPeriodes, setSelectedPeriodes] = useState<string[]>([]);

  const { data, isPending } = useQuery({
    queryKey: ["dataRekapVerifikasi", selectedPeriodes],
    queryFn: () => rekapVerifikasiUkt(selectedPeriodes),
  });

  const { data: listDataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  });

  const filteredListPeriode = listDataPeriode?.filter((periode: Periode) => {
    return (
      periode?.jalur_jenis?.nama_jalur_jenis_slug !== "pascasarjana" &&
      periode?.jalur_jenis?.nama_jalur_jenis_slug !== "profesi"
    );
  });

  // Kalkulasi Summary Keseluruhan
  const summary = useMemo(() => {
    if (!data) return { total: 0, done: 0, pending: 0, progress: 0 };

    const total = data.reduce(
      (acc: number, curr: ValidasiType) => acc + Number(curr.total_verifikasi),
      0,
    );
    const done = data.reduce(
      (acc: number, curr: ValidasiType) =>
        acc + Number(curr.jml_sudah_valid_final),
      0,
    );
    const pending = data.reduce(
      (acc: number, curr: ValidasiType) =>
        acc + Number(curr.jml_belum_valid_final),
      0,
    );
    const progress = total === 0 ? 0 : (done / total) * 100;

    return { total, done, pending, progress };
  }, [data]);

  return (
    <div className="flex flex-col gap-8 w-full pb-10">
      {/* HEADER SECTION */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 text-primary rounded-xl">
              <BarChart3 className="w-6 h-6" />
            </div>
            Rekapitulasi Verifikasi
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm sm:text-base ml-14">
            Pantau progress dan beban kerja masing-masing verifikator.
          </p>
        </div>
      </div>

      {/* SUMMARY CARDS (Dashboard Metrics) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
          <div className="p-3 sm:p-4 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-600 dark:text-slate-300">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Total Penugasan
            </p>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
              {isPending ? "..." : summary.total}{" "}
              <span className="text-sm font-normal text-slate-500">Berkas</span>
            </h3>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
          <div className="p-3 sm:p-4 bg-emerald-100 dark:bg-emerald-900/30 rounded-full text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Selesai Divalidasi
            </p>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
              {isPending ? "..." : summary.done}{" "}
              <span className="text-sm font-normal text-emerald-500">
                ({summary.progress.toFixed(1)}%)
              </span>
            </h3>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
          <div className="p-3 sm:p-4 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-600 dark:text-amber-400">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Menunggu Proses
            </p>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
              {isPending ? "..." : summary.pending}{" "}
              <span className="text-sm font-normal text-slate-500">Berkas</span>
            </h3>
          </div>
        </div>
      </div>

      {/* FILTER SECTION (Pills/Chips UI) */}
      <div className="sticky top-0 z-50 bg-white dark:bg-slate-900 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="px-3 flex items-center gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400 border-b sm:border-b-0 sm:border-r border-slate-200 dark:border-slate-700 pb-2 sm:pb-0 shrink-0">
          <Filter className="w-4 h-4" /> Filter Jalur:
        </div>
        <div className="flex flex-nowrap overflow-x-auto gap-2 p-1 custom-scrollbar pb-2 sm:pb-1">
          {filteredListPeriode?.map((periode: Periode) => {
            const id = periode?.id_sys_periode || "";
            const isSelected = selectedPeriodes.includes(id);

            return (
              <button
                key={id}
                onClick={() =>
                  setSelectedPeriodes((prev) =>
                    prev.includes(id)
                      ? prev.filter((p) => p !== id)
                      : [...prev, id],
                  )
                }
                className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  isSelected
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                {isSelected && <Check className="w-3.5 h-3.5" />}
                {periode?.jalur_jenis?.nama_jalur_jenis}
              </button>
            );
          })}
          {filteredListPeriode?.length === 0 && (
            <span className="text-sm text-slate-400 italic px-2 py-1">
              Tidak ada data periode tersedia.
            </span>
          )}
        </div>
      </div>

      {/* MAIN TABLE SECTION */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        {isPending ? (
          <SkeletonTable />
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 dark:bg-slate-900/80 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-6 py-4 font-semibold whitespace-nowrap w-[30%]">
                    Verifikator
                  </th>
                  <th className="px-6 py-4 font-semibold text-center whitespace-nowrap">
                    Belum Divalidasi
                  </th>
                  <th className="px-6 py-4 font-semibold text-center whitespace-nowrap">
                    Selesai
                  </th>
                  <th className="px-6 py-4 font-semibold text-center whitespace-nowrap">
                    Total Beban
                  </th>
                  <th className="px-6 py-4 font-semibold whitespace-nowrap w-[30%]">
                    Progress Capaian
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {data && data.length > 0 ? (
                  data.map((item: ValidasiType) => {
                    // Logic untuk Progress Bar Individu
                    const itemTotal = Number(item.total_verifikasi);
                    const itemDone = Number(item.jml_sudah_valid_final);
                    const percentage =
                      itemTotal === 0 ? 0 : (itemDone / itemTotal) * 100;

                    // Warna dinamis berdasarkan progress
                    const isComplete = percentage === 100;
                    const progressColor = isComplete
                      ? "bg-emerald-500"
                      : "bg-primary";

                    return (
                      <tr
                        key={item?.user_id}
                        className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                      >
                        {/* Kolom Verifikator dengan Avatar */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9 border border-slate-200 dark:border-slate-700">
                              <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                                {getInitials(item.nama_lengkap || "V")}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                              {item.nama_lengkap}
                            </span>
                          </div>
                        </td>

                        {/* Kolom Angka */}
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-md bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium">
                            {item.jml_belum_valid_final}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium">
                            {item.jml_sudah_valid_final}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-md bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-bold">
                            {item.total_verifikasi}
                          </span>
                        </td>

                        {/* Kolom Visualisasi Progress Bar */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3 w-full max-w-[200px]">
                            <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-1000 ease-out ${progressColor}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-xs font-bold text-slate-500 min-w-[2.5rem] text-right">
                              {percentage.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-slate-500 dark:text-slate-400"
                    >
                      <div className="flex flex-col items-center justify-center gap-2">
                        <BarChart3 className="w-8 h-8 opacity-20" />
                        <p>Belum ada data rekapitulasi verifikasi.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
